/**
 * Global "is the API reachable?" monitor.
 *
 * Why this exists: the SPA and the API are two separate IIS sites, deployed
 * separately. During a release the static SPA keeps being served while the API's
 * app pool is stopped (msdeploy's AppOffline rule) and then restarts to run
 * migrations. For that window every request the open page makes fails, and until
 * now each view swallowed it into its own catch — so the operator saw empty grids,
 * spinners that never end, and saves that appeared to do nothing, with no
 * indication that anything was happening. This turns that window into one honest
 * message, and clears it by itself the moment the API answers again.
 *
 * Design rule: a failing REQUEST never puts the overlay on screen. It only starts a
 * probe of the anonymous `/api/health` liveness endpoint, and only a failing PROBE
 * does. One report timing out must not black out someone's screen.
 *
 * The probe deliberately uses bare `axios`, not `helpers/api.js`: the api instance's
 * response interceptor calls back into this module, so probing through it would
 * recurse, and its request interceptor would attach credentials the liveness probe
 * has no use for.
 */
import axios from "axios";
import { ref, readonly } from "vue";
import {
  ANSWERING_SINCE_KEY,
  classifyApiFailure,
  classifyProbeFailure,
  HEARTBEAT_MS,
  nextAnsweringSince,
  nextProbeDelay,
  outageReasonFor,
  OUTAGE_GAME_CACHE_KEY,
  resolveShowGame,
  restoreAnsweringSince,
  shouldHeartbeat,
  showGameCacheValue,
} from "./apiAvailability.mjs";

/**
 * online    — nothing wrong (no overlay)
 * checking  — a request failed; probing /health. Still NO overlay: most suspicions
 *             die here, and flashing a maintenance panel for 400ms is worse than
 *             showing nothing.
 * offline   — a probe failed. Overlay is up.
 * recovered — the API answered after an outage. Overlay switches to "back online".
 */
const _status = ref("online");
/**
 * Why the overlay is up — the latest probe's verdict, so it follows the server as a
 * deploy moves from "app pool stopped" to "started and crashed" to "answering":
 *   "server"  — the API is unreachable or IIS answers 502/503/504 (update in progress)
 *   "errored" — the API ANSWERED the probe, but not with a 200 (crashed on startup)
 *   "network" — this device has no connection
 */
const _reason = ref("server");
/** Epoch ms the outage started, or null. Drives the elapsed counter in the overlay. */
const _since = ref(null);

export const apiStatus = readonly(_status);
export const apiOutageReason = readonly(_reason);
export const apiOutageSince = readonly(_since);

// How long after the first suspicion the confirming probe runs. Long enough that a
// burst of parallel failures collapses into one probe, short enough to feel instant.
const FIRST_PROBE_DELAY_MS = 400;
// The probe must give up well inside the shortest backoff step, or probes overlap.
const PROBE_TIMEOUT_MS = 6000;
// How long the "back online" panel stays before it fades itself out.
const RECOVERED_LINGER_MS = 8000;
// The panel config is a nicety: never let a slow answer hold anything up.
const CONFIG_TIMEOUT_MS = 5000;

let timer = null;
let recoveredTimer = null;
let attempt = 0;
// When the server began answering without the app (see ERRORED_AFTER_MS), or null. Kept in
// sessionStorage so a refresh mid-outage does not restart the clock.
let answeringSince = restoreAnsweringSince(readSession(ANSWERING_SINCE_KEY), Date.now());
// Last idle heartbeat, so tab-switching cannot turn the visibility check into a request storm.
let lastHeartbeatAt = 0;
const HEARTBEAT_MIN_GAP_MS = 5000;
// Bumped whenever the API is seen alive, so a probe round that was still waiting on the
// network when that happened cannot put the panel back up.
let onlineGeneration = 0;

function apiBase() {
  const base = process.env.VUE_APP_API_BASE_URL || "http://localhost:5293/api";
  return base.replace(/\/+$/, "");
}

function healthUrl() {
  return `${apiBase()}/health`;
}

function readSession(key) {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function setAnsweringSince(value) {
  answeringSince = value;
  try {
    if (value === null) sessionStorage.removeItem(ANSWERING_SINCE_KEY);
    else sessionStorage.setItem(ANSWERING_SINCE_KEY, String(value));
  } catch {
    /* storage unavailable — the in-memory value still holds for this page */
  }
}

function readCachedShowGame() {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem(OUTAGE_GAME_CACHE_KEY) : null;
  } catch {
    return null; // private mode / blocked storage — fall back to the setting's default
  }
}

const _showGame = ref(resolveShowGame(readCachedShowGame()));
/** Whether the outage panel offers its game — the last answer the API gave, cached. */
export const outageShowGame = readonly(_showGame);

/**
 * Kept as a seam so this file stays line-for-line comparable with the admin SPA's copy,
 * which suppresses the monitor inside the server-side print renderer. This app has no
 * such route, so nothing is suppressed here.
 */
function suppressed() {
  return false;
}

function clearTimers() {
  if (timer) { clearTimeout(timer); timer = null; }
}

function scheduleProbe(delayMs) {
  clearTimers();
  timer = setTimeout(runProbe, delayMs);
}

async function runProbe() {
  timer = null;

  // A device with its radio off will fail the probe too, but the honest message is
  // "you are offline", not "the server is being updated".
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    setAnsweringSince(null);
    markOffline("network");
    scheduleProbe(nextProbeDelay(attempt++));
    return;
  }

  const generation = onlineGeneration;
  try {
    await axios.get(healthUrl(), {
      timeout: PROBE_TIMEOUT_MS,
      // Cache-bust: a proxy that cached one 200 would make the probe lie forever.
      params: { _t: Date.now() },
      headers: { "Cache-Control": "no-cache" },
    });
    markOnline();
  } catch (err) {
    // A server that answers the probe with a 500 is a different situation from one that
    // does not answer at all, and the panel must say so. When the answer is readable that
    // is immediate; cross-origin, IIS's own pages are not, so fall back to whether anything
    // answered and for how long — see ERRORED_AFTER_MS.
    const verdict = classifyProbeFailure(err);
    const answered = verdict === "errored" || (await serverAnswers());
    if (generation !== onlineGeneration) return; // the API came back meanwhile — that wins
    const now = Date.now();
    setAnsweringSince(nextAnsweringSince(answeringSince, answered, now));
    markOffline(outageReasonFor(verdict, answeringSince, now));
    scheduleProbe(nextProbeDelay(attempt++));
  }
}

/**
 * Did ANYTHING answer the liveness URL? A `no-cors` request resolves on any HTTP response —
 * IIS's 500.30 page included, whose status a cross-origin page is never shown — and rejects
 * only when nothing answered (refused, DNS, timeout). The `reach` marker tells it apart from
 * the real probe in server logs and in the e2e routes.
 */
async function serverAnswers() {
  if (typeof fetch !== "function" || typeof AbortController === "undefined") return false;
  const controller = new AbortController();
  const abort = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    await fetch(`${healthUrl()}?reach=1&_t=${Date.now()}`, {
      mode: "no-cors",
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(abort);
  }
}

function markOffline(reason) {
  _reason.value = reason;
  if (_status.value !== "offline") {
    _status.value = "offline";
    _since.value = Date.now();
  }
}

function markOnline() {
  clearTimers();
  attempt = 0;
  setAnsweringSince(null);
  onlineGeneration++;
  // The "back online" panel owns its own dismissal. Without this guard the very next
  // successful response — and after an outage there is one within milliseconds, from the
  // views that retry — would drop the panel before anyone could read it or press Reload.
  if (_status.value === "recovered") return;
  if (_status.value === "offline") {
    _status.value = "recovered";
    // Views that gave up mid-outage can listen for this and refetch.
    if (typeof window !== "undefined") window.dispatchEvent(new Event("api:online"));
    // The release may have changed the switch; pick it up for the next outage.
    refreshOutagePanelConfig();
    if (recoveredTimer) clearTimeout(recoveredTimer);
    recoveredTimer = setTimeout(dismissRecovered, RECOVERED_LINGER_MS);
  } else {
    _status.value = "online";
    _since.value = null;
  }
}

/** Called by the axios ERROR interceptor for every failed request. */
export function notifyApiFailure(error) {
  if (suppressed()) return;
  if (classifyApiFailure(error) !== "suspect") return;
  // Already offline or already probing — never restart the ladder, or a page full of
  // failing widgets would keep the backoff pinned at its shortest step.
  if (_status.value === "offline" || _status.value === "checking") return;
  _status.value = "checking";
  attempt = 0;
  scheduleProbe(FIRST_PROBE_DELAY_MS);
}

/** Called by the axios SUCCESS interceptor. Any 2xx proves the API is up. */
export function notifyApiSuccess() {
  if (_status.value === "online") {
    // Hot path. A streak restored from a refresh is stale the moment the API answers.
    if (answeringSince !== null) setAnsweringSince(null);
    return;
  }
  markOnline();
}

/**
 * Idle check: a page that makes no requests would otherwise never notice the API going away,
 * and the panel appeared only after the user clicked something or refreshed. A plain GET with no
 * custom headers needs no CORS preflight, so this costs one small request per visible tab.
 */
async function heartbeat() {
  if (suppressed()) return;
  const visibility = typeof document !== "undefined" ? document.visibilityState : "visible";
  if (!shouldHeartbeat(_status.value, visibility)) return;
  const now = Date.now();
  if (now - lastHeartbeatAt < HEARTBEAT_MIN_GAP_MS) return;
  lastHeartbeatAt = now;
  try {
    await axios.get(healthUrl(), { timeout: PROBE_TIMEOUT_MS, params: { _t: now } });
    notifyApiSuccess();
  } catch (err) {
    notifyApiFailure(err);
  }
}

/** "Retry now" in the overlay: probe immediately instead of waiting out the backoff. */
export function retryNow() {
  if (_status.value !== "offline") return;
  scheduleProbe(0);
}

/** Dismiss the "back online" panel. */
export function dismissRecovered() {
  if (recoveredTimer) { clearTimeout(recoveredTimer); recoveredTimer = null; }
  if (_status.value === "recovered") {
    _status.value = "online";
    _since.value = null;
  }
}

/**
 * Read the panel's own switch while the API is up, so it is known when the API is not.
 * Bare axios on purpose, like the probe: a failure here must never start the outage
 * ladder, and nothing here ever throws — the last cached answer simply stands.
 */
export async function refreshOutagePanelConfig() {
  if (suppressed()) return;
  try {
    const { data } = await axios.get(`${apiBase()}/auth/login-config`, {
      timeout: CONFIG_TIMEOUT_MS,
      params: { _t: Date.now() },
      headers: { "Cache-Control": "no-cache" },
    });
    const value = showGameCacheValue(data);
    if (value === null) return;
    _showGame.value = resolveShowGame(value);
    try {
      localStorage.setItem(OUTAGE_GAME_CACHE_KEY, value);
    } catch {
      /* storage unavailable — the in-memory answer still holds for this page */
    }
  } catch {
    /* API down or older than the setting — keep the last known answer */
  }
}

/** Reload the SPA — after a deploy the served bundle has new chunk hashes. */
export function reloadApp() {
  if (typeof window !== "undefined") window.location.reload();
}

// A device coming back onto a network is a free, instant signal — don't sit out the
// remaining backoff waiting to rediscover it.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    if (_status.value === "offline") scheduleProbe(0);
  });
  setInterval(heartbeat, HEARTBEAT_MS);
  // Returning to a tab is exactly when a user expects the page to be current.
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") heartbeat();
    });
  }
}
